import {
  Card,
  Descriptions,
  Header,
  IconMagicSolid,
  Space,
  SubText,
  TransparentIconButton,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { CatalogItemDetailsAnalysisStyled } from './styles/CatalogItemDetailsAnalysisStyled'
import { CatalogItemDetailsAnalysisTextStyled } from './styles/CatalogItemDetailsAnalysisTextStyled'
import { CatalogItemDetailsBodyStyled } from './styles/CatalogItemDetailsBodyStyled'
import { CatalogItemDetailsImageStyled } from './styles/CatalogItemDetailsImageStyled'
import { CatalogItemDetailsImageWrapperStyled } from './styles/CatalogItemDetailsImageWrapperStyled'
import { CatalogItemDetailsLayoutStyled } from './styles/CatalogItemDetailsLayoutStyled'
import { CatalogItemDetailsSourceRowStyled } from './styles/CatalogItemDetailsSourceRowStyled'
import { CatalogItemDetailsCart } from '../CatalogItemDetailsCart'
import { CatalogItemTag, type ICatalogItemTagProps } from '../CatalogItemTag'

export interface ICatalogItemDetail {
  label: string
  value: string
}

export interface ICatalogItemDetailsProps {
  img?: string
  title: string
  sku: string
  tag: ICatalogItemTagProps
  price: number
  currency: CurrencyName
  agentAnalysis: string
  catalogDetails: ICatalogItemDetail[]
  quantity: number
  estimatedTotal: number
  onQuantityChange: (quantity: number) => void
  onAddToRequest: () => void
  onCollapse: () => void
}

export const CatalogItemDetails = ({
  img,
  title,
  sku,
  tag,
  price,
  currency,
  agentAnalysis,
  catalogDetails,
  quantity,
  estimatedTotal,
  onQuantityChange,
  onAddToRequest,
  onCollapse,
}: ICatalogItemDetailsProps) => {
  const intl = useProcurifyIntl()

  return (
    <CatalogItemDetailsLayoutStyled>
      <Card.Header>
        <TransparentIconButton
          icon='chevron-thick-right'
          aria-label={intl.formatMessage(messages.collapseAriaLabel)}
          analyticsId='catalog-item-details-collapse-button'
          onClick={onCollapse}
        />
      </Card.Header>

      <CatalogItemDetailsBodyStyled>
        <Space direction='vertical' size='xlarge'>
          <Space direction='vertical' size='medium'>
            {img && (
              <CatalogItemDetailsImageWrapperStyled>
                <CatalogItemDetailsImageStyled src={img} alt='' />
              </CatalogItemDetailsImageWrapperStyled>
            )}
            <Space direction='vertical' size='xsmall'>
              <Header as='h4'>
                <span className='fs-mask'>{title}</span>
              </Header>
              <SubText color='darkGrey' className='fs-mask'>
                {intl.formatMessage(messages.sku, { sku })}
              </SubText>
            </Space>
            <CatalogItemDetailsSourceRowStyled>
              <CatalogItemTag
                icon={tag.icon}
                text={tag.text}
                variant={tag.variant}
              />
              <span className='fs-mask'>
                <Price inline price={price} currency={currency} />
              </span>
            </CatalogItemDetailsSourceRowStyled>
          </Space>

          <Space direction='vertical' size='small'>
            <Space direction='horizontal' size='small' align='center'>
              <IconMagicSolid block size={20} aria-hidden />
              <Header as='h5'>
                {intl.formatMessage(messages.agentAnalysisTitle)}
              </Header>
            </Space>
            <CatalogItemDetailsAnalysisStyled>
              <CatalogItemDetailsAnalysisTextStyled className='fs-mask'>
                {agentAnalysis}
              </CatalogItemDetailsAnalysisTextStyled>
            </CatalogItemDetailsAnalysisStyled>
          </Space>

          <Space direction='vertical' size='small'>
            <Header as='h5'>
              {intl.formatMessage(messages.catalogDetailsTitle)}
            </Header>
            <Descriptions valueWidth='fill'>
              {catalogDetails.map((detail, index) => (
                <Descriptions.Item
                  key={`${index}-${detail.label}`}
                  label={detail.label}
                >
                  {detail.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Space>
        </Space>
      </CatalogItemDetailsBodyStyled>

      <Card.Footer isSticky>
        <CatalogItemDetailsCart
          quantity={quantity}
          estimatedTotal={estimatedTotal}
          currency={currency}
          onQuantityChange={onQuantityChange}
          onAddToRequest={onAddToRequest}
        />
      </Card.Footer>
    </CatalogItemDetailsLayoutStyled>
  )
}
